import type { ErrorInfo, ReactNode } from 'react';

import { Component } from 'react';

import Flex from '@ui/Flex';
import Graphic from '@ui/Graphic';

import { getErrorDetail, type ErrorDetail } from './getErrorDetail';

interface FallbackProps {
  /**
   * The raw error that was thrown.
   */
  error: Error;
  /**
   * Normalized error details that parses out things like status code and
   * concatenated info messages if the error was an API error.
   */
  errorDetail: ErrorDetail;
  /**
   * Additional info about the error provided by React, such as the component
   * stack trace.
   */
  errorInfo: ErrorInfo;
}

interface BaseProps {
  children?: ReactNode;
  className?: string;
  /**
   * A required unique identifier for this error boundary. When changed, the
   * error boundary will reset. This is useful when navigating between pages so
   * that errors don't persist across page views.
   *
   * @example
   * ```tsx
   * // a static key won't reset the boundary and when triggered it will remain until unmounted
   * <ErrorBoundary key="my-error-boundary" />
   * ```
   *
   * @example
   * ```tsx
   * // a dynamic key will reset the boundary whenever the slug changes
   * <ErrorBoundary key={`my-error-boundary-${action}-${section}-${slug}`} />
   * ```
   */
  key: React.Key;
  /** Optional onError callback for logging to Sentry */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

export type PropsWithRender = BaseProps & {
  // if a `fallbackRender` is provided, `fallback` must be undefined and vice versa
  fallback?: never;
  /**
   * [Render
   * prop](https://react.dev/reference/react/Children#calling-a-render-prop-to-customize-rendering)
   * function responsible for returning a fallback UI based on a thrown value.
   *
   * ```tsx
   * <ErrorBoundary fallbackRender={({ error }) => <div>{error.message}</div>} />
   * ```
   */
  fallbackRender: (props: FallbackProps) => ReactNode;
};

export type PropsWithFallback = BaseProps & {
  /**
   * Static content to render in place of an error if one is thrown.
   *
   * ```tsx
   * <ErrorBoundary fallback={<div>Something went wrong</div>} />
   * ```
   */
  fallback?: ReactNode;
  fallbackRender?: never;
};

type Props = PropsWithFallback | PropsWithRender;

interface State {
  error?: Error;
  errorInfo?: ErrorInfo;
  hasError: boolean;
}

/**
 * Generic ErrorBoundary class component that can be used to wrap any component and catch errors w/ fallback UI.
 *
 * Follows React guidelines: https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 *
 * Note: There is currently no way to write an error boundary as a functional component unless we were to use a library like [react-error-boundary](https://github.com/bvaughn/react-error-boundary).
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props;

    this.setState({ hasError: true, error, errorInfo });

    onError?.(error, errorInfo);
  }

  render() {
    const { children } = this.props;
    const { hasError } = this.state;

    const fallback = this.props.fallback || (
      <Flex align="center" className={this.props.className} gap="xs" justify="center" layout="col">
        <Graphic name="warning" size="lg" />
        Something went wrong!
      </Flex>
    );

    if (hasError && this.state.error && this.state.errorInfo && this.props.fallbackRender) {
      return this.props.fallbackRender({
        error: this.state.error,
        errorDetail: getErrorDetail(this.state.error),
        errorInfo: this.state.errorInfo,
      });
    } else if (hasError) {
      return fallback;
    }

    return children;
  }
}

export default ErrorBoundary;
